import { request } from '@/api/apiUtils';
import { EMPLOYEES_ROOT } from '@/data/entities/employees';
import { PROJECTS_ROOT } from '@/data/entities/projects';
import { getCrossCuttingPrefixes, useCreate, useCustomMutation, useDelete, useUpdate } from '@/data/mutations';
import type { ICollaborator, ImportStatistics, IProject, ProjectType, ResponseType } from '@/types';

export const useCreateProject = useCreate.project<FormData, ResponseType<IProject>>({
    endpoint: () => 'projects',
    body: data => data,
});

export const useUpdateProject = useUpdate.project<Partial<ProjectType>, ResponseType<IProject>>({
    method: 'PUT',
    endpoint: ({ id }) => `projects/${id}`,
    body: params => params,
});

export const useUpdateProjectAssets = useUpdate.project<{ id: number; data: FormData }, ResponseType<IProject>>({
    endpoint: ({ id }) => `projects/${id}?_method=PUT`,
    body: ({ data }) => data,
});

export function useAssignProjectCollaborators() {
    return useCustomMutation<ICollaborator, ResponseType<IProject>>({
        mutationFn: params =>
            request<ResponseType<IProject>>(`projects/${params.id}/assignees`, {
                method: 'POST',
                body: JSON.stringify(params),
            }),
        invalidate: [PROJECTS_ROOT, EMPLOYEES_ROOT],
    });
}

export const useCopyProject = useCreate.project<{ project_id: number }, void>({
    endpoint: ({ project_id }) => `projects/${project_id}/copy`,
});

/** Soft delete — the project moves to the deleted list (from active or
 *  archived); `DELETE projects/:id/force` is the hard one. */
export const useDeleteProject = useDelete.project<{ project_id: number }, void>({
    method: 'DELETE',
    endpoint: ({ project_id }) => `projects/${project_id}`,
});

/** Hard delete, for a project nobody should find again (a half-seeded
 *  tutorial project). The backend allows it only for a live project with no
 *  scenes: a trashed one answers 404, one with scenes 422. */
export const useForceDeleteProject = useDelete.project<{ project_id: number }, void>({
    method: 'DELETE',
    endpoint: ({ project_id }) => `projects/${project_id}/force`,
});

/** Moves an active or deleted project to the archive. */
export const useArchiveProject = useUpdate.projectArchive<{ project_id: number }, void>({
    method: 'PUT',
    endpoint: ({ project_id }) => `projects/${project_id}/archive`,
});

/** Brings an archived or deleted project back to the active list. */
export const useRestoreProject = useUpdate.projectRestore<{ project_id: number }, void>({
    method: 'PUT',
    endpoint: ({ project_id }) => `projects/${project_id}/restore`,
});

/**
 * Script import returns ImportStatistics (not a project response). Only the
 * character list refreshes here (the union dialog reads it immediately) —
 * everything else the import touches refetches via the SSE flow.
 */
export function useImportScript() {
    return useCustomMutation<{ project_id: number; data: FormData }, ImportStatistics>({
        mutationFn: ({ project_id, data }) =>
            request<ImportStatistics>(`projects/${project_id}/episodes/upload`, { method: 'POST', body: data }),
        invalidate: getCrossCuttingPrefixes('project.import'),
    });
}
